import { create } from 'zustand';

import { de } from '../i18n/de';
import { seedSettled, wasFreshInstall } from '../data/seed';
import { useUiStore } from '../app/uiStore';

/**
 * `Dieses Gerät ist neu. Backup wiederherstellen?` — shown once, on the launch that created the database.
 *
 * 4.7 asks for first-run detection that offers restore **before** setup, and the failure it exists to prevent
 * is specific: a caregiver sets up a replacement iPad by hand over an evening, then discovers the restore
 * button and has to choose between discarding their work and discarding months of the person's learned
 * vocabulary. Offering first means that choice never arises.
 *
 * ── Why it lives in the reserved band, and not in a banner ──
 *
 * It was a banner first, and the flow suite caught what that cost: **the geometry guard failed on every
 * surface.** A banner is added height, the vertical budget has 7 pt of slack in 768, and eating it drops
 * targets below the 16 mm floor — a non-negotiable, on every fresh install, not just in a test (C56). The band
 * is already reserved and, on the one launch this can appear, already empty.
 *
 * ── Why the state is in a store rather than in the component ──
 *
 * The band now has five possible tenants and one precedence (`bandTenant.ts`), which means the *owner* of the
 * band has to know whether this offer applies before deciding to render it. A component cannot answer a question
 * that decides whether it mounts, so the answer lives outside it — and `startRestoreOffer()` runs at boot with
 * the other `start*` functions rather than inside a component that may never render.
 */

interface OfferState {
  fresh: boolean;
  dismissed: boolean;
}

const useOfferState = create<OfferState>(() => ({ fresh: false, dismissed: false }));

/** Whether the offer applies. Read by the band's owner to decide the tenant. */
export const useRestoreOffer = (): boolean =>
  useOfferState((state) => state.fresh && !state.dismissed);

/**
 * Resolve the one question this offer rests on, once, at boot.
 *
 * `wasFreshInstall()` is true exactly once in the life of an install — the next launch finds quick fires and
 * seeds nothing — so "have we already asked?" is a question that never has to be stored or remembered.
 */
export function startRestoreOffer(): void {
  void seedSettled.then(() => {
    useOfferState.setState({ fresh: wasFreshInstall() });
  });
}

export function RestoreOffer(): React.JSX.Element | null {
  const applies = useRestoreOffer();
  const openCare = useUiStore((s) => s.openCare);

  if (!applies) return null;

  return (
    // No `ContentBand` and no height of its own: `PredictionRow` owns the band's frame, so that the promise
    // about its height is kept in one place rather than in each of four tenants (ADR-039).
    <div style={{ display: 'flex', gap: 'var(--key-gap)', height: '100%', flex: '1 1 0', minWidth: 0 }}>
      <button
        type="button"
        data-testid="restore-offer"
        onClick={() => openCare('backup')}
        style={{
          flex: '1 1 auto',
          height: '100%',
          padding: '4px 16px',
          fontSize: 'var(--font-button)',
          fontWeight: 700,
          color: 'var(--fg)',
          background: 'var(--bg)',
          border: '4px solid var(--fg)',
          borderRadius: 'var(--radius)',
        }}
      >
        {de.restoreOffer}
      </button>
      {/* Declining is a first-class choice with its own target, not an × in a corner. Most fresh installs
          really are new devices, and a caregiver should be able to say so in one confident tap. */}
      <button
        type="button"
        data-testid="restore-offer-dismiss"
        onClick={() => useOfferState.setState({ dismissed: true })}
        style={{
          flex: '0 0 auto',
          height: '100%',
          minWidth: 'var(--min-target)',
          padding: '4px 16px',
          fontSize: 'var(--font-body)',
          color: 'var(--fg)',
          background: 'var(--bg)',
          border: '3px solid var(--key-border)',
          borderRadius: 'var(--radius)',
        }}
      >
        {de.restoreOfferDismiss}
      </button>
    </div>
  );
}
